import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { Image3D } from '~/domain/patient/image3d';
import { useSelectStep } from '~/routes/workflowStepParam';
import { useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { DomainError } from '~/domain/errors/DomainError';
import { readStructureSetsOnImage } from '~/domain/services/structureSetService/readStructureSetsOnImage';
import {
    DetachedContourResult,
    detachedContourResultsState,
} from '~/state/atoms/detachedContourResultsAtom';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { ensureStructureSetsCurrent } from '~/hooks/ensureStructureSetsCurrent';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { autoSegmentationTaskState } from '~/state/atoms/structureSet/autoSegmentationTaskAtom';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import useProcessDicomFiles from '../../../useProcessDicomFiles';
import useViewportEnvironment from '../../../useViewportEnvironment';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useContouringBackend, useSessionID } from '../useBackend';
import { logErrorName } from '~/utils/logErrorName';
import { isSessionDerivedStructureSet } from './sessionDerivedStructureSet';

export interface AutoSegmentationOptions {
    image?: Image3D;
    isBackgroundComparison?: boolean;
}

/**
 * A React hook for creating an auto-segmentation task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useAutoSegmentation() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const primaryImage = useSelectedPrimaryImage();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const session = useSessionOrNull();
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [autoSegmentationTask, setAutoSegmentationTaskState] = useRecoilState(
        autoSegmentationTaskState
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const setDetachedResults = useSetRecoilState(detachedContourResultsState);
    const selectStep = useSelectStep();
    const { workingStep } = useWorkflowSteps();

    const contouringClient = useContouringBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentStructureSet } = useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const finalFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(job.results)
                : job.results;

            if (finalFiles) {
                const generationModel = transformTaskToGenerationModel(
                    job.task
                );
                const processedItems = await processCreatedFiles(
                    finalFiles,
                    generationModel
                );
                if (processedItems) {
                    updateJob(job.id, { isImported: true });
                    setEnvironmentStructureSet(
                        processedItems.structureSets,
                        selectedPatientId ?? ''
                    );
                }
            } else {
                handleError('Re-identification failed for one or more files.');
            }
        }
    };

    const cacheDetachedResult = async (job: Job, image: Image3D) => {
        const record = (result: DetachedContourResult) =>
            setDetachedResults((current) => ({ ...current, [job.id]: result }));
        try {
            const results = job.results ?? [];
            const files = job.payload?.pseudonymized
                ? await reidentifyFiles(results)
                : results;
            if (!files) {
                record({
                    kind: 'failed',
                    messageId: 'replanning.contour.reidentificationFailed',
                });
                return;
            }
            const structureSets = readStructureSetsOnImage(
                await Promise.all(files.map((file) => file.arrayBuffer())),
                image,
                transformTaskToGenerationModel(job.task)
            );
            if (structureSets.length !== 1) {
                record({
                    kind: 'failed',
                    messageId: 'replanning.contour.unexpectedSetCount',
                    detail: String(structureSets.length),
                });
                return;
            }
            updateJob(job.id, { isImported: true });
            record({ kind: 'ready', structureSet: structureSets[0] });
        } catch (error) {
            logErrorName('Contour+ result could not be read:', error);
            record({
                kind: 'failed',
                messageId: 'replanning.contour.unreadable',
                detail:
                    error instanceof DomainError ? error.message : undefined,
            });
        }
    };

    const handleSubmit = async (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        customizationName?: string,
        options: AutoSegmentationOptions = {}
    ): Promise<string | undefined> => {
        const { image, isBackgroundComparison = false } = options;
        const targetImage = image ?? primaryImage;
        if (isSubmitting && !isBackgroundComparison) {
            return;
        }

        let submittedJobId: string | undefined;
        if (targetImage) {
            setIsSubmitting(true);
            try {
                const runSessionId = resetSessionID();
                const runClient = isBackgroundComparison
                    ? contouringClient.withSession(runSessionId)
                    : contouringClient;
                const taskUID = `${runClient.sessionID}.${v4().split('-')[0]}`;
                const blobs = Array.from(targetImage.getSlicesAsDicomBlobs());
                // Only with the displayed scan: the current set was drawn on it.
                if (
                    !isBackgroundComparison &&
                    targetImage === primaryImage &&
                    autoSegmentationTask?.includeStructureSet &&
                    currentStructureSet
                ) {
                    if (
                        !ensureStructureSetsCurrent(
                            getDatasetEnvironmentRegistry(),
                            [currentStructureSet],
                            'Auto-segmentation'
                        )
                    ) {
                        return;
                    }
                    if (!session) {
                        throw new Error(
                            'No active patient session for auto-segmentation.'
                        );
                    }
                    if (
                        !isSessionDerivedStructureSet(
                            session,
                            currentStructureSet.dicomUid
                        )
                    ) {
                        blobs.push(
                            session.structureSetExportService.exportBlob(
                                currentStructureSet.dicomUid
                            )
                        );
                    }
                }

                const blobsToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, blobs)
                    : blobs;

                if (!isBackgroundComparison) {
                    setAutoSegmentationTaskState((state) => ({
                        ...state,
                        model: selectedModel,
                        subSelectedStructures: selectedStructures,
                        customizationName,
                    }));
                }

                const modelIdentifier = customizationName
                    ? `${selectedModel.name}.${customizationName}`
                    : selectedModel.name;

                const backendParameters: RequestParameters = {
                    action: modelIdentifier,
                    taskUID,
                    nExpectedFiles: blobs.length.toString(),
                };
                if (selectedStructures.length > 0) {
                    backendParameters.rois = JSON.stringify(selectedStructures);
                }

                if (blobsToUpload) {
                    const contourJob: NewJobData = {
                        // A background run gets its own id: `updateJob` writes to every job sharing one.
                        id: isBackgroundComparison ? taskUID : sessionID,
                        name: 'Contour+',
                        totalItems: blobsToUpload.length,
                        type: WorkflowTypes.CONTOUR,
                        client: runClient,
                        // A background run stays out of the patient: it is compared against, never shown.
                        onLoad: isBackgroundComparison
                            ? (job: Job) =>
                                  cacheDetachedResult(job, targetImage)
                            : handleProcessedFiles,
                        payload: {
                            filesToUpload: blobsToUpload.map((file) => ({
                                file,
                            })),
                            backendParameters,
                            pseudonymized: pseudonymize,
                        },
                        task: {
                            model: selectedModel,
                            subSelectedStructures: selectedStructures,
                            includeStructureSet:
                                autoSegmentationTask?.includeStructureSet,
                        },
                    };

                    addJob(contourJob);
                    submittedJobId = contourJob.id;
                    if (!isBackgroundComparison) {
                        setWorkflowModalState((state) => ({
                            ...state,
                            open: false,
                        }));
                        if (workingStep.id !== 'contouring') {
                            selectStep('contouring');
                        }
                    }
                } else {
                    handleError('Anonymization failed for one or more files.');
                }
            } catch (error) {
                handleError(error, 'Failed to start auto-segmentation task.');
            } finally {
                setIsSubmitting(false);
            }
        }
        return submittedJobId;
    };

    return { startProcessing: handleSubmit, isSubmitting };
}
